import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowRight, Play, Trophy, Award } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import heroPortrait from "@/assets/hero-portrait.jpg";
import remaxAlfaLogo from "@/assets/remax-alfa-logo.png";

const ease = [0.22, 1, 0.36, 1] as const;

const rotatingWords = ["podpisu", "prodeji", "náběru"];

const HeroSection = () => {
  const [wordIndex, setWordIndex] = useState(0);

  useEffect(() => {
    const interval = setInterval(() => {
      setWordIndex(i => (i + 1) % rotatingWords.length);
    }, 3000);
    return () => clearInterval(interval);
  }, []);
  return (
    <section className="relative min-h-screen flex items-center overflow-hidden">
      {/* Background gradient */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 1.2 }}
        className="absolute inset-0 bg-gradient-to-br from-muted/50 via-background to-secondary/30"
      />
      
      {/* Subtle grid pattern */}
      <div className="absolute inset-0 opacity-[0.015]" style={{
        backgroundImage: "linear-gradient(hsl(var(--foreground)) 1px, transparent 1px), linear-gradient(90deg, hsl(var(--foreground)) 1px, transparent 1px)",
        backgroundSize: "60px 60px"
      }} />

      <div className="relative container-luxury section-padding w-full">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
          {/* Text content */}
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, ease }}
            className="order-2 lg:order-1"
          >
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2, ease }}
              className="flex items-center gap-2 sm:gap-3 mb-6 overflow-x-auto scrollbar-hide"
            >
              <img src={remaxAlfaLogo} alt="RE/MAX Alfa" className="h-10" />
              <div className="h-6 w-px bg-border/40" />
              {[
                { icon: Trophy, text: "5× TOP 10", delay: 0.5 },
                { icon: Award, text: "15× Nejlepší", delay: 0.65 },
              ].map(({ icon: Icon, text, delay }) => (
                <motion.div
                  key={text}
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.4, delay, ease }}
                  whileHover={{ scale: 1.05 }}
                  className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border border-primary/25 bg-primary/[0.08] backdrop-blur-sm shadow-sm hover:shadow-md hover:bg-primary/[0.12] transition-all duration-300 cursor-default"
                >
                  <div className="flex items-center justify-center w-5 h-5 rounded-full shrink-0" style={{ background: 'linear-gradient(135deg, #002349, hsl(215 65% 42%))' }}>
                    <Icon className="h-2.5 w-2.5 text-white" strokeWidth={2.5} />
                  </div>
                  <span className="text-[11px] font-bold text-foreground/85 tracking-wide whitespace-nowrap">{text}</span>
                  <span className="text-[10px] text-muted-foreground/70 font-medium whitespace-nowrap">v REMAX ČR</span>
                </motion.div>
              ))}
            </motion.div>

            <motion.h1
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.4, ease }}
              className="relative text-4xl sm:text-5xl lg:text-6xl xl:text-7xl font-heading font-bold leading-[1.05] tracking-tight text-foreground mb-6 overflow-hidden"
            >
              {/* Glass shine sweep */}
              <motion.span
                initial={{ x: "-100%" }}
                animate={{ x: "200%" }}
                transition={{ duration: 1.8, delay: 1.2, ease: [0.22, 1, 0.36, 1] }}
                className="absolute inset-0 z-10 pointer-events-none"
                style={{
                  background: "linear-gradient(105deg, transparent 40%, hsl(0 0% 100% / 0.35) 45%, hsl(0 0% 100% / 0.6) 50%, hsl(0 0% 100% / 0.35) 55%, transparent 60%)",
                  width: "60%",
                }}
              />
              30 let zkušeností{" "}
              <span className="text-primary">
                v každém{" "}
                <AnimatePresence mode="wait">
                  <motion.span
                    key={rotatingWords[wordIndex]}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -20 }}
                    transition={{ duration: 0.4, ease }}
                    className="inline-block"
                  >
                    {rotatingWords[wordIndex]}.
                  </motion.span>
                </AnimatePresence>
              </span>
            </motion.h1>

            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.55, ease }}
              className="text-lg sm:text-xl text-muted-foreground leading-relaxed max-w-xl mb-10 font-light"
            >
              Ve světě realit se trendy mění, ale hodnota slova a poctivé práce zůstává. Prodám vaši nemovitost s nadhledem experta a technologiemi dneška.
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.7, ease }}
              className="flex flex-col sm:flex-row gap-4"
            >
              <Button size="lg" className="btn-glass-hover group text-base px-8 py-6 rounded-xl bg-primary text-primary-foreground hover:bg-primary/90 shadow-lg hover:shadow-xl transition-all duration-300" asChild>
                <Link to="/sluzby/odhad">
                  Chci zjistit cenu své nemovitosti
                  <ArrowRight className="ml-2 h-4 w-4 transition-transform group-hover:translate-x-1" />
                </Link>
              </Button>
              <Button variant="outline" size="lg" className="text-base px-8 py-6 rounded-xl border-border hover:bg-secondary transition-all duration-300" asChild>
                <a href="#nabidka">
                  <Play className="mr-2 h-4 w-4" />
                  Moje aktuální nabídka
                </a>
              </Button>
            </motion.div>
          </motion.div>

          {/* Portrait */}
          <motion.div
            initial={{ opacity: 0, scale: 0.9, y: 30 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            transition={{ duration: 1.1, delay: 0.3, ease }}
            className="order-1 lg:order-2 flex justify-center lg:justify-end mb-16 lg:mb-0"
          >
            <div className="relative overflow-visible">
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 1.5, delay: 0.5 }}
                className="absolute -inset-4 bg-gradient-to-tr from-primary/10 via-transparent to-accent/5 rounded-3xl blur-2xl"
              />
              <img
                src={heroPortrait}
                alt="František Babčický - realitní makléř"
                width={440}
                height={500}
                fetchPriority="high"
                loading="eager"
                decoding="async"
                className="relative w-80 sm:w-96 lg:w-[440px] max-h-[500px] rounded-3xl object-cover object-top shadow-2xl"
              />
              {/* Floating stat card */}
              <motion.div
                initial={{ opacity: 0, y: 30, scale: 0.9 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                transition={{ duration: 0.7, delay: 1.1, ease, type: "spring", stiffness: 200 }}
                className="absolute -bottom-8 left-1/2 -translate-x-1/2 glass-card p-4 px-8 text-center"
              >
                <div className="text-3xl font-heading font-bold text-primary">1670+</div>
                <div className="text-sm text-muted-foreground whitespace-nowrap">prodaných nemovitostí</div>
              </motion.div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default HeroSection;
